import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Sorting.module.css';
import { generateUnsortedArray } from '../../lib/arrayGenerator';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const MERGE_SORT_CODE = `function mergeSort(arr, l, r) {
  if (l >= r) {
    return;
  }
  const m = Math.floor((l + r) / 2);
  mergeSort(arr, l, m);
  mergeSort(arr, m + 1, r);
  merge(arr, l, m, r);
}

function merge(arr, left, mid, right) {
  const leftArr = arr.slice(left, mid + 1);
  const rightArr = arr.slice(mid + 1, right + 1);
  let i = 0, j = 0, k = left;

  while (i < leftArr.length && j < rightArr.length) {
    if (leftArr[i] <= rightArr[j]) {
      arr[k++] = leftArr[i++];
    } else {
      arr[k++] = rightArr[j++];
    }
  }

  while (i < leftArr.length) arr[k++] = leftArr[i++];
  while (j < rightArr.length) arr[k++] = rightArr[j++];
}`;

const MergeSort: React.FC = () => {
  const navigate = useNavigate();
  const [array, setArray] = useState<number[]>([]);
  const [isSorting, setIsSorting] = useState(false);
  const [highlightedIndices, setHighlightedIndices] = useState<number[]>([]);
  const [sortedIndices, setSortedIndices] = useState<number[]>([]);
  const [highlightedLine, setHighlightedLine] = useState<number | null>(null);
  const stopSort = useRef(false);

  const handleStop = useCallback(() => {
    stopSort.current = true;
    setIsSorting(false);
  }, []);

  const handleRandomize = useCallback(() => {
    stopSort.current = true;
    setIsSorting(false);
    setHighlightedIndices([]);
    setSortedIndices([]);
    setHighlightedLine(null);
    setArray(generateUnsortedArray(15, 100));
  }, []);

  useEffect(() => {
    handleRandomize();
    return () => {
      stopSort.current = true;
    };
  }, [handleRandomize]);

  const handleGoBack = () => navigate(-1);

  const merge = async (arr: number[], l: number, m: number, r: number) => {
    const n1 = m - l + 1;
    const n2 = r - m;
    let L = new Array(n1);
    let R = new Array(n2);

    for (let i = 0; i < n1; i++) L[i] = arr[l + i];
    for (let j = 0; j < n2; j++) R[j] = arr[m + 1 + j];
    
    let i = 0, j = 0, k = l;
    while (i < n1 && j < n2) {
      if (stopSort.current) return;
      setHighlightedLine(18); 
      setHighlightedIndices([l + i, m + 1 + j]);
      await delay(300);

      if (L[i] <= R[j]) {
        setHighlightedLine(19); 
        arr[k] = L[i];
        i++;
      } else {
        setHighlightedLine(21); 
        arr[k] = R[j];
        j++;
      }
      setArray([...arr]);
      await delay(300);
      k++;
    }

    while (i < n1) {
      if (stopSort.current) return;
      setHighlightedLine(25); 
      arr[k] = L[i];
      setArray([...arr]);
      await delay(300);
      i++;
      k++;
    }

    while (j < n2) {
      if (stopSort.current) return;
      setHighlightedLine(26); 
      setHighlightedIndices([m + 1 + j]);
      arr[k] = R[j];
      setArray([...arr]);
      await delay(300);
      j++;
      k++;
    }
  };

  const mergeSortHelper = async (arr: number[], l: number, r: number) => {
    if (stopSort.current) return;
    setHighlightedLine(2);
    if (l >= r) {
      if (l >= 0 && l < arr.length) setSortedIndices(prev => [...prev, l]);
      return;
    }
    
    setHighlightedLine(5);
    const m = l + Math.floor((r - l) / 2);
    await delay(250);

    setHighlightedLine(6);
    await mergeSortHelper(arr, l, m);
    if (stopSort.current) return;
    await delay(250);

    setHighlightedLine(7);
    await mergeSortHelper(arr, m + 1, r);
    if (stopSort.current) return;
    await delay(250);

    setHighlightedLine(8);
    await merge(arr, l, m, r);
    if (stopSort.current) return;
    await delay(250);
    
    const sortedRange = Array.from({ length: r - l + 1 }, (_, i) => l + i);
    setSortedIndices(prev => Array.from(new Set([...prev, ...sortedRange])));
  };
  
  const runMergeSort = async () => {
    stopSort.current = false;
    setHighlightedIndices([]);
    setSortedIndices([]);
    setHighlightedLine(null);
    setIsSorting(true);

    let arrCopy = [...array];
    await mergeSortHelper(arrCopy, 0, arrCopy.length - 1);
    
    if (!stopSort.current) {
      setSortedIndices(Array.from(Array(arrCopy.length).keys()));
    }
    setHighlightedIndices([]);
    setHighlightedLine(null);
    setIsSorting(false);
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Merge Sort</h1>
      
        <div className={styles.description}>
          <h2>Description</h2>
          <p>Merge Sort is a stable, "divide and conquer" algorithm. It divides the array into halves, sorts them recursively, and then merges the two sorted halves back together.</p>
        </div>
        <div className={styles.useCase}>
          <h3>When to use it?</h3>
          <ul>
            <li>When you need a stable sort (it preserves the order of equal elements).</li>
            <li>When a guaranteed worst-case performance of O(n log n) is required.</li>
          </ul>
        </div>
        <div className={styles.timeComplexity}>
          <h3>Time Complexity</h3>
          <p>
            <b>Best/Average/Worst Case:</b> O(n log n)
          </p>
        </div>

      <div className={styles.visualization}>
        {array.map((value, index) => (
          <div
            key={index}
            className={`
              ${styles.bar}
              ${highlightedIndices.includes(index) ? styles.comparing : ''}
              ${sortedIndices.includes(index) ? styles.sorted : ''}
            `}
            style={{ height: `${value * 3.5}px` }}
          >
            <span className={styles.barValue}>{value}</span>
          </div>
        ))}
      </div>

      <div className={styles.controls}>
        <button onClick={runMergeSort} disabled={isSorting}>Run Merge Sort</button>
        <button onClick={handleRandomize} disabled={isSorting}>Randomize</button>
        <button onClick={handleStop} disabled={!isSorting}>Stop</button>
      </div>

      <div className={styles.codeDisplay}>
        {MERGE_SORT_CODE.split('\n').map((line, index) => (
          <div key={index} className={`${styles.codeLine} ${highlightedLine === index + 1 ? styles.highlightedLine : ''}`}>
            {line}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MergeSort;